import { CheckCircleIcon } from "Icons/CheckCircle";
import { RefreshCw02Icon } from "Icons/RefreshCw02";

import { css } from "@emotion/react";

import { useRouter } from "next/router";

import { ShareWithRecruitersToggle } from "components/recruit/candidate/ShareWithRecruitersToggle";
import { Disclaimer } from "components/shared/library/Disclaimer";
import { SpinningIcon } from "components/shared/library/SpinningIcon";
import { WhiteLabelButton } from "components/shared/library/WhiteLabelButton";

import { useAccount } from "contexts/AccountContext";
import { useInterview } from "contexts/InterviewContext";

import { useBeforeUnload } from "utils/libraryHooks";
import { isViewMobile } from "utils/mediaQueryStrings";
import { formatCounter } from "utils/time";
import { replaceQueryParams } from "utils/urls";

import { uiGray } from "constants/colors";

import { CandidateFlowCardHeader } from "./CandidateFlowCardHeader";

const style = {
  buttons: css({
    display: "flex",
    flexDirection: "column",
    width: "-webkit-fill-available",
    gap: 8,
    flexShrink: 0,
  }),
  shareInterview: css({
    padding: 24,
    border: `1px solid ${uiGray[10]}`,
    borderRadius: 16,
  }),
};

type Props = { secondsIn: number; isVideoUploading: boolean };
export const CallEnded = ({ secondsIn, isVideoUploading }: Props) => {
  const { accountProfile } = useAccount(true);
  const router = useRouter();

  const { interviewFlowId, interviewDetails } = useInterview();

  useBeforeUnload(isVideoUploading);

  return (
    <>
      <CandidateFlowCardHeader
        Icon={isVideoUploading ? SpinningIcon : CheckCircleIcon}
        title={`Your interview has ended at ${formatCounter(secondsIn)}`}
        subtitle={
          isVideoUploading
            ? "Hang tight while we finish processing your interview!"
            : "Your interview is done processing."
        }
      />
      {isVideoUploading && (
        <Disclaimer
          type={"warning"}
          title={
            "Do not close this window. Leaving the screen now will not save and send the interview properly to your recruiter."
          }
        />
      )}
      {interviewDetails?.interviewType === "recruitment" &&
        interviewDetails.isCandidateAllowedToPublish && (
          <div css={style.shareInterview}>
            <ShareWithRecruitersToggle
              interviewFlowId={interviewFlowId}
              candidateId={accountProfile.id}
              display="full"
              isPublishedByDefault={true}
              orgName={interviewDetails?.orgName}
            />
          </div>
        )}

      <div css={style.buttons}>
        {(interviewDetails?.retriesLimit ?? Infinity) > 0 && (
          <WhiteLabelButton
            isFullWidth
            color="gray"
            IconLeft={RefreshCw02Icon}
            onClick={() => location.reload()}
            background="outlined"
            disabled={isVideoUploading}
          >
            Start over
          </WhiteLabelButton>
        )}
        <WhiteLabelButton
          isFullWidth
          onClick={() => replaceQueryParams(router, "step", "feedback")}
          disabled={isVideoUploading}
        >
          {isVideoUploading
            ? `Analyzing${isViewMobile() ? "" : " your responses..."}`
            : "Done"}
        </WhiteLabelButton>
      </div>
    </>
  );
};
